/**
 * Хуки каталога ролей тенанта (§7.4 «Управление ролями»).
 *
 * Бэк: internal/authorization — GET/POST /v1/admin/roles, PATCH/DELETE
 * /v1/admin/roles/{id}. ListRoles требует ?tenant_id=<uuid>, поэтому хук
 * сначала тянет /v1/me (там tenant_id из JWT) и только потом грузит роли.
 *
 * RBAC: /v1/admin/roles гейтится для управляющих ролей (owner/manager) —
 * см. role-exception prefixes в cmd/api/main.go. Персонал сюда не ходит.
 *
 * permissions всегда массив строк. Создание/правка роли с пустым permissions
 * передаёт [] (НЕ {} объект — иначе роль молча выпадает из RBAC, патент
 * feedback_malformed_role_perms_drops_rbac).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listRoles, createRole, updateRole, deleteRole } from "@hsc/api-client";
import type { Role, RoleCreateInput, RoleUpdateInput } from "@hsc/types";
import { useMe } from "../../shared/api/useMe.js";

export const ROLES_KEY = ["roles"] as const;

/** Каталог ролей текущего тенанта. */
export function useRoles() {
  const me = useMe();
  const tenantId = me.data?.tenant_id;
  const query = useQuery<Role[]>({
    queryKey: [...ROLES_KEY, tenantId] as const,
    queryFn: async () => (await listRoles(tenantId as string)).items,
    enabled: typeof tenantId === "string" && tenantId !== "",
    staleTime: 30_000,
  });
  return {
    roles: query.data ?? [],
    tenantId,
    isLoading: me.isLoading || query.isLoading,
    isError: me.isError || query.isError,
    error: query.error,
  };
}

/** Создание кастомной роли тенанта. */
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation<Role, Error, RoleCreateInput>({
    mutationFn: createRole,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

/** Обновление набора прав роли. */
export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation<Role, Error, { id: string; input: RoleUpdateInput }>({
    mutationFn: ({ id, input }) => updateRole(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

/** Удаление кастомной роли. */
export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteRole,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}
